import React, { useState, useEffect } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import Reveal from './Reveal';
import { SectionMarker } from './Gap';
import CaseStudies from './CaseStudies';
import { IMG } from '../data';

// Showreel sources: single 16:9 master hosted locally for reliable delivery
const showreelSrc = '/showreel-new.mp4';
const showreelSrcVertical = null;

// Real client logos lifted from empactstudio.com
const brands = [
  { name: 'Wizcraft', src: '/logos/wizcraft.jpg' },
  { name: 'Tata Elxsi', src: '/logos/tata-elxsi.jpeg' },
  { name: 'E Factor Experiences', src: '/logos/e-factor.jpeg' },
  { name: 'Creative Factory', src: '/logos/creative-factory.png' },
  { name: 'Wedding Line', src: '/logos/wedding-line.png' },
  { name: 'Maximus', src: '/logos/maximus.png' },
  { name: 'Mirage', src: '/logos/mirage.png' },
  { name: 'Neo Niche', src: '/logos/neo-niche.png' },
  { name: '70 Event Group', src: '/logos/70-event-group.png' },
  { name: '77 Entertainment', src: '/logos/77-entertainment.png' },
  { name: 'Aura', src: '/logos/aura.png' },
  { name: 'Netflix', src: '/logos/netflix.png' },
  { name: 'Communique', src: '/logos/communique.png' },
  { name: 'Pixadoo Visuals', src: '/logos/pixadoo.png' },
  { name: 'Evoke Media', src: '/logos/evoke-media.webp' },
  { name: 'Modern Stage Services', src: '/logos/modern-stage-services.jpg' },
  { name: 'Production Crew', src: '/logos/production-crew.jpeg' },
  { name: 'Renaissance', src: '/logos/renaissance.png' },
  { name: 'Swaaha Wedding', src: '/logos/swaaha-wedding.jpeg' },
];

const Work = () => {
  const [playing, setPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const vertical = isMobile && !!showreelSrcVertical;
  const activeSrc = vertical ? showreelSrcVertical : showreelSrc;
  const posterSrc = '/showreel-poster.jpg';
  const frameAspect = 'aspect-video';

  return (
    <section id="work" className="relative section-pad border-t border-[var(--border)] scroll-mt-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal><SectionMarker index="VI" label="Magic we create" className="label-gap" /></Reveal>
        <div className="hidden">
          <Reveal>
            <h2 className="font-display font-medium text-[clamp(1.6rem,3vw,2.6rem)] leading-[1.15] tracking-[-0.01em] w-full">
              There is only so much a website can explain about an experience. Eventually you have to <span className="font-serif-c italic text-[var(--accent)] font-normal">experience it in person</span>: the installations, the interaction, the technology, the details and the people who bring it to life.
            </h2>
          </Reveal>
        </div>

        {/* Single showreel - clean poster, click to play */}
        <Reveal>
          <div className="group relative rounded-2xl overflow-hidden border border-[var(--border)] cursor-pointer" onClick={() => setPlaying(true)}>
            <div className={`${frameAspect} overflow-hidden bg-[var(--surface)]`}>
              {playing && activeSrc ? (
                <video
                  ref={(el) => { if (el) { el.play().catch(() => {}); } }}
                  src={activeSrc}
                  poster={posterSrc}
                  className={`w-full h-full ${vertical ? 'object-cover' : 'object-cover'} bg-black`}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  data-testid="showreel-video"
                />
              ) : (
                <img src={posterSrc} alt="Empax showreel" className="w-full h-full object-cover" />
              )}
            </div>

            {!(playing && activeSrc) && (
              <>
                {/* blueprint grid lines for engineered texture */}
                <div className="pointer-events-none absolute inset-0 opacity-[0.14]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
                <span className="pointer-events-none absolute top-5 left-5 w-6 h-6 border-l-2 border-t-2 border-[var(--accent)]/70" />
                <span className="pointer-events-none absolute bottom-5 right-5 w-6 h-6 border-r-2 border-b-2 border-[var(--accent)]/70" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src="/empax-logo.png" alt="Empax Studio" className="absolute top-[16%] md:top-[18%] left-1/2 -translate-x-1/2 h-7 md:h-24 w-auto opacity-95 drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)]" />
                  <span
                    data-testid="showreel-play"
                    className="relative w-16 h-16 md:w-24 md:h-24 rounded-full bg-[var(--accent)] flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110"
                  >
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-[var(--accent)]/40 animate-ping" />
                    <Play size={26} className="relative text-[#0A0D17] ml-1 md:hidden" fill="#0A0D17" />
                    <Play size={30} className="relative text-[#0A0D17] ml-1 hidden md:block" fill="#0A0D17" />
                  </span>
                </div>
              </>
            )}
          </div>
        </Reveal>

        {/* Case studies */}
        <CaseStudies />

        {/* Brand marquee */}
        <div className="mt-16 md:mt-40 pt-10 md:pt-16 border-t border-[var(--border)]">
          <Reveal><p className="label !text-[var(--accent)] mb-3">Experiences we have built for</p></Reveal>
          <div className="overflow-hidden py-10 mt-6">
            <div className="marquee-track">
              {[...brands, ...brands].map((b, i) => (
                <div
                  key={i}
                  data-testid={`brand-logo-${i}`}
                  className="mx-2 md:mx-5 shrink-0 inline-flex items-center justify-center h-14 md:h-24 w-32 md:w-48 p-2.5 md:p-3 bg-white rounded-xl md:rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.25)]"
                >
                  <img src={b.src} alt={b.name} className="max-w-full max-h-full w-auto h-auto object-contain" decoding="async" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Work;
